import type { TranslationTree } from '../../types';

export const traceability: TranslationTree = {
  traceability: {
    formPage: {
      hero: {
        badge: 'Rückverfolgungsformular',
        title: 'Werkzeug prüfen und Meldungen erfassen',
        description:
          'Überprüfen Sie die gescannten Werkzeugdaten, bestätigen Sie den Zustand und erfassen Sie die Produktionsparameter für eine lückenlose Rückverfolgbarkeit.',
        breadcrumb: 'Rückverfolgbarkeit erfassen',
      },
      invalid: {
        title: 'Werkzeug nicht gefunden',
        description:
          'Die angegebene Kennung ist keinem registrierten Werkzeug zugeordnet. Prüfen Sie, ob ein Lese- oder Eingabefehler vorliegt.',
        backButton: 'Zurück zum Scan',
      },
      steps: {
        badge: 'Eingabeschritte',
        title: 'Produktionsbedingungen erfassen',
        description:
          'Füllen Sie die folgenden Abschnitte aus, um eine neue Meldung zu speichern. Die Angaben werden Schritt für Schritt validiert.',
      },
    },
    toolInfo: {
      badge: 'Werkzeugvalidierung',
      actions: {
        close: 'Schließen',
      },
      loading: 'Informationen werden abgerufen…',
      errorDefault: 'Fehler beim Laden der Daten.',
      fields: {
        reference: 'Referenz',
        company: 'Unternehmen',
        totalTraces: 'Erfasste Meldungen',
        latestStatus: 'Letzter Status',
      },
      fallback: {
        notProvided: 'Nicht angegeben',
      },
      latestReasonPrefix: 'Letzter Grund:',
    },
    scanPage: {
      hero: {
        badge: 'Scan-Sitzung',
        title: 'Identifizieren Sie Ihre Werkzeuge im Handumdrehen',
        description:
          'Positionieren Sie das Werkzeug vor der Kamera, um seine Kennung automatisch zu erkennen, oder geben Sie sie bei Bedarf manuell ein.',
        breadcrumb: 'Rückverfolgbarkeit scannen',
      },
      tabs: {
        camera: 'Kamera',
        keyence: 'Keyence SR-X300',
      },
      scan: {
        title: 'Automatischer Scan',
        description:
          'Halten Sie das Werkzeug vor die Kamera. Nach der Erkennung öffnet sich das Rückverfolgungsformular automatisch.',
        overlay: {
          detected: 'Werkzeug erkannt: {{id}}',
          waiting: 'Warte auf Erkennung… halten Sie das Werkzeug ruhig.',
          active: 'Scan aktiv',
        },
        tips: {
          lighting:
            'Verwenden Sie einen neutralen Hintergrund und vermeiden Sie Spiegelungen, um den Scan zu beschleunigen.',
          switchCamera:
            'Die Kamera kann unten rechts gewechselt werden, wenn Sie mit einem Smartphone scannen.',
        },
      },
      manual: {
        title: 'Manuelle Kennungseingabe',
        description:
          'Wählen Sie das Werkzeug aus der Liste oder geben Sie die ID vom Werkzeug-Label ein.',
        label: 'Werkzeug-ID',
        placeholder: 'z. B. TL-4823',
        submit: 'ID bestätigen',
      },
      dev: {
        title: 'Entwicklungsmodus',
        description: 'Überspringen Sie den Scan, um das Formular ohne Kamera zu testen.',
        button: 'Testformular öffnen',
      },
      keyence: {
        title: 'Keyence SR-X300 Scanner',
        description:
          'Konfigurieren Sie die intelligente Kamera und lösen Sie einen Scan aus, um den DataMatrix-Code zu lesen.',
        backendLabel: 'Backend-URL',
        ipLabel: 'Kamera-IP-Adresse',
        backendPlaceholder: 'z. B. https://toollife-datamatrix.local:44312',
        ipPlaceholder: 'z. B. 192.168.100.100',
        connect: 'Verbinden',
        disconnect: 'Trennen',
        connecting: 'Verbinden…',
        disconnecting: 'Trennen…',
        scan: 'Scannen',
        scanning: 'Scannen…',
        cancel: 'Abbrechen',
        detected: 'Werkzeug erkannt',
        accept: 'Akzeptieren und fortfahren',
        error: 'Fehler',
        status: {
          connected: 'Mit Kamera verbunden',
          disconnected: 'Nicht verbunden',
        },
        errors: {
          connectionFailed: 'Verbindung zur Kamera fehlgeschlagen',
          disconnectionFailed: 'Trennung von der Kamera fehlgeschlagen',
          scanFailed: 'Scan fehlgeschlagen',
          cancelFailed: 'Abbruch des Scans fehlgeschlagen',
          networkError: 'Netzwerkfehler - Backend-URL prüfen',
        },
        tips: {
          network:
            'Stellen Sie sicher, dass die Keyence-Kamera mit demselben Netzwerk verbunden ist.',
          position: 'Positionieren Sie das Werkzeug im Sichtfeld der Kamera, bevor Sie auslösen.',
          backend: 'Der Backend-Dienst verwaltet die Kommunikation mit der Kamera.',
        },
        advanced: {
          title: 'Erweiterte Einstellungen',
        },
        info: {
          title: 'Funktionsweise',
          description: 'Der Keyence SR-X300 liest DataMatrix-Codes aus der Ferne.',
          step1: 'Geben Sie die Backend-URL und die Kamera-IP-Adresse ein.',
          step2: 'Klicken Sie auf "Verbinden", um eine Verbindung zur Kamera herzustellen.',
          step3: 'Drücken Sie "Scannen", um die Kennung zu erfassen und zu dekodieren.',
        },
      },
      camera: {
        title: 'Automatischer Scan',
        description:
          'Halten Sie das Werkzeug vor die Kamera. Nach der Erkennung öffnet sich automatisch das Rückverfolgbarkeitsformular.',
        detected: 'Werkzeug erkannt',
        scanning: 'Scannen…',
        tips: {
          lighting:
            'Verwenden Sie einen neutralen Hintergrund und vermeiden Sie Blendung, um das Lesen des Codes zu beschleunigen.',
          position: 'Halten Sie das Werkzeug ruhig im Sichtfeld der Kamera.',
        },
      },
      addTool: {
        button: 'Werkzeug erstellen',
      },
    },
    machineManager: {
      title: 'Maschinenverwaltung',
      description:
        'Fügen Sie Maschinen aus Ihrem Bestand hinzu, aktualisieren Sie deren Merkmale und synchronisieren Sie die Auswahl mit dem Formular.',
      confirmDelete: 'Bestätigen',
      errors: {
        nameRequired: 'Die Maschinenkennung ist erforderlich.',
        selectBeforeDelete: 'Wählen Sie vor dem Löschen eine Maschine aus.',
        notFound: 'Die ausgewählte Maschine existiert nicht mehr.',
        apiNotReady: 'API nicht bereit',
        fetchFailed: 'Fehler beim Laden der Maschinen',
        saveFailed: 'Fehler beim Speichern der Maschine',
        deleteFailed: 'Das Löschen von Maschinen ist noch nicht implementiert.',
        noData: 'Keine Maschinendaten zurückgegeben',
      },
      form: {
        sectionTitle: 'Maschinenblatt',
        fields: {
          name: {
            label: 'Maschinenkennung',
            placeholder: 'z. B. DMG Mori DMC 65 monoBLOCK',
          },
          brand: {
            label: 'Marke',
            placeholder: 'z. B. DMG Mori',
          },
          model: {
            label: 'Modell',
            placeholder: 'Modell eingeben',
          },
          serial: {
            label: 'Seriennummer',
            placeholder: 'Interne oder Herstellerkennung',
          },
          kafkaName: {
            label: 'Signalquelle',
            placeholder: 'Keine Signalquelle',
            help: 'Die Messung der Maschine in der Signaldatenbank (InfluxDB). Die Signalseite und die Live-Vorhersagen lesen sie.',
            groupActive: 'Aktiv',
            groupHistory: 'Nur Verlauf',
            historyOnlyWarning:
              'Keine Daten in der letzten Stunde: Live-Vorhersagen lesen das zuletzt aufgezeichnete Teil und geben sein Alter an.',
            loadError: 'Die Liste der Signalquellen konnte nicht geladen werden.',
            retry: 'Erneut versuchen',
          },
        },
        submit: {
          save: 'Speichern',
          update: 'Aktualisieren',
          saving: 'Wird gespeichert...',
        },
        new: 'Neues Blatt',
      },
      helper:
        'Die Maschinen werden lokal gespeichert, um die Eingabe zu erleichtern. Aktualisieren Sie die Daten bei Wartungen oder Änderungen.',
      list: {
        title: 'Registrierte Maschinen',
        count: '{{count}} Eintrag(e)',
        empty: 'Noch keine Maschine registriert.',
        serial: 'SN: {{serial}}',
        sourceBadgeTitle: 'Signalquelle',
      },
      delete: {
        button: 'Auswahl löschen',
        deleting: 'Wird gelöscht...',
      },
    },
    processManager: {
      form: {
        sectionTitle: 'Prozessblatt',
        fields: {
          name: {
            label: 'Prozessname',
            placeholder: 'z. B. Schlichten EGO2-40',
          },
          type: {
            label: 'Prozesstyp',
            standard: 'Standard',
            test: 'Test',
          },
          toolReference: {
            label: 'Werkzeugreferenz',
            placeholder: '— auswählen —',
            noOptions: 'Keine Werkzeugreferenzen verfügbar',
          },
          material: {
            label: 'Material',
            none: '— keines —',
          },
          materialLot: {
            label: 'Materialcharge',
            none: 'Keine Charge',
            placeholder: '— Charge auswählen —',
            disabled: 'Zuerst ein Material auswählen',
          },
          article: {
            label: 'Artikel',
            none: '— keiner —',
          },
          machines: {
            label: 'Maschinen',
            placeholder: '— Maschine hinzufügen —',
          },
        },
        showToOperator: 'Dem Bediener anzeigen',
        durationWarning: 'Keine Bearbeitungsdauer ist vorausgefüllt — der Bediener wird bei jeder Rückverfolgung mit diesem Prozess nach der Dauer pro Stück gefragt.',
        submit: {
          save: 'Prozess speichern',
          update: 'Änderungen speichern',
          saving: 'Wird gespeichert...',
          duplicate: 'Duplizieren',
          duplicateHint: 'Diesen Prozess kopieren ohne zu speichern',
        },
        new: 'Neuer leerer Prozess',
        editingPill: 'Bearbeitung — {{name}}',
        notSet: '— Nicht gesetzt —',
        sections: {
          cuttingParameters: {
            title: 'Schnittparameter',
            toggleLabel: 'Abschnitt Schnittparameter ein-/ausblenden',
          },
          machinedResults: {
            title: 'Bearbeitungsergebnisse',
            toggleLabel: 'Abschnitt Bearbeitungsergebnisse ein-/ausblenden',
          },
          operatorInfo: {
            title: 'Bedienerinfo',
            toggleLabel: 'Abschnitt Bedienerinfo ein-/ausblenden',
          },
          tooling: {
            title: 'Werkzeug & Spannung',
            toggleLabel: 'Abschnitt Werkzeug & Spannung ein-/ausblenden',
          },
          countLabel: '{{set}}/{{total}} gesetzt',
        },
        prefillFields: {
          ae: {
            label: 'ae',
            placeholder: 'z. B. 2,5',
            unit: 'mm',
          },
          ap: {
            label: 'ap',
            placeholder: 'z. B. 5,0',
            unit: 'mm',
          },
          vc: {
            label: 'vc',
            placeholder: 'z. B. 120',
            unit: 'm/min',
          },
          fz: {
            label: 'fz',
            placeholder: 'z. B. 0,05',
            unit: 'mm/Zahn',
          },
          machinedVolume: {
            label: 'Bearbeitetes Volumen',
            placeholder: 'z. B. 10',
            unit: 'cm³',
          },
          machinedDistance: {
            label: 'Bearbeitete Strecke',
            placeholder: 'z. B. 50',
            unit: 'm',
          },
          machinedPieces: {
            label: 'Bearbeitete Teile',
            placeholder: 'z. B. 100',
            unit: 'Stk.',
          },
          totalMachiningDuration: {
            label: 'Gesamtbearbeitungsdauer',
            placeholder: 'z. B. 3600',
            unit: 's',
          },
          pieceMachiningDuration: {
            label: 'Dauer pro Teil',
            placeholder: 'z. B. 36',
            unit: 's',
          },
          malround: {
            label: 'Malround',
            placeholder: 'z. B. 5',
            unit: 'µm',
          },
          operator: {
            label: 'Bediener',
            placeholder: 'z. B. M. Müller',
          },
          operation: {
            label: 'Operation',
            placeholder: 'z. B. Schlichtdurchgang',
          },
          lubType: {
            label: 'Schmierungstyp',
            placeholder: 'z. B. Emulsion',
          },
          lubBrand: {
            label: 'Schmiermittelmarke',
            placeholder: 'z. B. Blaser B-Cool',
          },
          holderBrand: {
            label: 'Werkzeughaltermarke',
            placeholder: 'z. B. REGO-FIX',
          },
          holderType: {
            label: 'Werkzeughaltertyp',
            placeholder: 'z. B. Spannzangenfutter',
          },
          clampingType: {
            label: 'Spanntyp',
            placeholder: 'z. B. Schraubstock',
          },
          clampingBrand: {
            label: 'Spannmarke',
            placeholder: 'z. B. Hainbuch',
          },
        },
      },
      list: {
        title: 'Registrierte Prozesse',
        count: '{{count}} Prozess(e)',
        empty: 'Noch keine Prozesse registriert.',
        showArchived: 'Archivierte anzeigen',
        archivedBadge: 'Archiviert',
        searchPlaceholder: 'Nach Name suchen…',
        filterAll: 'Alle',
        filterStandard: 'Standard',
        filterTest: 'Test',
      },
      archive: {
        button: 'Archivieren',
        confirm: 'Archivierung bestätigen',
        archiving: 'Wird archiviert...',
      },
      restore: {
        button: 'Wiederherstellen',
      },
      errors: {
        apiNotReady: 'API nicht bereit',
        fetchFailed: 'Fehler beim Laden der Prozesse',
        noData: 'Keine Prozessdaten zurückgegeben',
        nameRequired: 'Der Prozessname ist erforderlich.',
        toolReferenceRequired: 'Die Werkzeugreferenz ist erforderlich.',
        saveFailed: 'Fehler beim Speichern des Prozesses',
      },
    },
    processSelection: {
      title: 'Prozess auswählen, um bekannte Felder zu überspringen',
      skip: 'Leer starten',
      prefilled: '{{count}} Felder vorausgefüllt',
      youFill: '{{count}} auszufüllen',
      type: {
        standard: 'Standard',
        test: 'Test',
      },
      chip: {
        label: 'Vorlage',
        hidden: '{{count}} Felder ausgeblendet',
        change: 'Ändern',
        clear: 'Löschen',
      },
      loading: 'Prozessvorlagen werden geladen…',
      errorTitle: 'Prozessvorlagen konnten nicht geladen werden',
      errorDescription: 'Sie können die Nachverfolgung dennoch durch manuelles Ausfüllen des Formulars erfassen.',
      errorRetry: 'Erneut versuchen',
      sections: {
        cutting: 'Schnittparameter',
        machined: 'Bearbeitungsergebnisse',
        operatorInfo: 'Bediener & Vorgang',
        machine: 'Maschine',
        tooling: 'Werkzeugaufnahme & Spannung',
        material: 'Werkstoff',
        toolState: 'Werkzeugzustand',
      },
      machineFallback: 'Maschine auswählen…',
      emptyColumn: 'Keine Prozesse dieses Typs',
    },
    materialManager: {
      form: {
        sectionTitle: 'Werkstoffblatt',
        fields: {
          name: {
            label: 'Werkstoffname',
            placeholder: 'z. B. X5CrNi18-10 Edelstahl',
          },
          isoCategory: {
            label: 'ISO-Kategorie',
            placeholder: '— auswählen —',
          },
          designation: {
            label: 'Bezeichnung',
            placeholder: 'z. B. X5CrNi18-10',
          },
          description: {
            label: 'Beschreibung',
            placeholder: 'Optionale Hinweise zu diesem Werkstoff',
          },
        },
        submit: {
          save: 'Speichern',
          update: 'Aktualisieren',
          saving: 'Wird gespeichert...',
        },
        new: 'Neuer Werkstoff',
      },
      list: {
        title: 'Gespeicherte Werkstoffe',
        count: '{{count}} Werkstoff(e)',
        empty: 'Noch keine Werkstoffe gespeichert.',
        emptyTitle: 'Noch keine Werkstoffe',
        emptyHint: 'Fügen Sie den ersten Werkstoff hinzu, um Ihren Katalog aufzubauen.',
        showArchived: 'Archivierte anzeigen',
        archivedBadge: 'Archiviert',
      },
      archive: {
        button: 'Archivieren',
        confirm: 'Archivierung bestätigen',
        archiving: 'Wird archiviert...',
      },
      restore: {
        button: 'Wiederherstellen',
      },
      errors: {
        apiNotReady: 'API nicht bereit',
        fetchFailed: 'Werkstoffe konnten nicht geladen werden',
        nameRequired: 'Werkstoffname ist erforderlich.',
        isoCategoryRequired: 'ISO-Kategorie ist erforderlich.',
        saveFailed: 'Werkstoff konnte nicht gespeichert werden',
        noData: 'Keine Werkstoffdaten zurückgegeben',
      },
    },
    articleManager: {
      form: {
        sectionTitle: 'Artikelblatt',
        fields: {
          name: {
            label: 'Artikelname',
            placeholder: 'z. B. Aluminium-Flansch 40mm',
          },
        },
        submit: {
          save: 'Speichern',
          update: 'Aktualisieren',
          saving: 'Wird gespeichert...',
        },
        new: 'Neuer Artikel',
      },
      list: {
        title: 'Registrierte Artikel',
        count: '{{count}} Artikel',
        empty: 'Noch keine Artikel registriert.',
        showArchived: 'Archivierte anzeigen',
        archivedBadge: 'Archiviert',
      },
      archive: {
        button: 'Archivieren',
        confirm: 'Archivierung bestätigen',
        archiving: 'Wird archiviert...',
      },
      restore: {
        button: 'Wiederherstellen',
      },
      errors: {
        apiNotReady: 'API nicht bereit',
        fetchFailed: 'Fehler beim Laden der Artikel',
        nameRequired: 'Der Artikelname ist erforderlich.',
        saveFailed: 'Fehler beim Speichern des Artikels',
        noData: 'Keine Artikeldaten zurückgegeben',
      },
    },
    form: {
      stepCounter: 'Schritt {{current}} von {{total}}',
      sending: 'Wird gesendet…',
      cancel: 'Übermittlung abbrechen',
      errorDefault: 'Beim Abrufen der Daten ist ein Fehler aufgetreten.',
      missingFields: '{{count}} Feld(er) müssen vor dem Absenden ausgefüllt werden.',
      missingFieldsNamed: 'Fehlende Pflichtfelder: {{fields}}',
      scanPopup: {
        title: 'Werkzeug scannen',
        description: 'Halten Sie das Werkzeug vor die Kamera, um seine Kennung automatisch zu erkennen.',
        helper: 'Halten Sie die Markierung scharf und gut beleuchtet, um das Lesen zu beschleunigen.',
        status: {
          waiting: 'Warten auf Erkennung… halten Sie das Werkzeug ruhig.',
          detected: 'Werkzeug erkannt: {{id}}',
        },
        actions: {
          cancel: 'Abbrechen',
        },
      },
      retry: 'Erneut versuchen',
      backHome: 'Zurück zur Startseite',
      success: 'Meldung erfolgreich übermittelt.',
      newScan: 'Neuer Scan',
      previous: 'Zurück',
      next: 'Weiter',
      submit: 'Absenden',
      stepAriaLabel: 'Schritt {{number}}: {{label}}, {{status}}',
      stepStatusCompleted: 'abgeschlossen',
      stepStatusCurrent: 'aktuell',
      stepStatusUpcoming: 'ausstehend',
      validation: {
        requiredMultiTooltip:
          'Mindestens eines der mit einem gelben * markierten Felder muss ausgefüllt werden.',
        requiredMultiAtLeastOne: 'Mindestens ein Feld muss ausgefüllt werden.',
        checkboxAtLeastOne: 'Bitte wählen Sie mindestens eine Option aus.',
      },
    },
    steps: {
      general: 'Allgemein',
      material: 'Material',
      machine: 'Maschine',
      holder: 'Werkzeughalter',
      clamping: 'Werkstückspannung',
      lubrication: 'Schmierung',
      break: 'Werkzeugzustand',
    },
    generalTab: {
      durationsTitle: 'Bearbeitungszeiten',
      durationsDescription:
        'Geben Sie die Gesamtzeit oder die Zeit pro Stück an. Beide Werte helfen, den Werkzeugverschleiß zu beobachten.',
      durationsRequiredCounter: '1 von 2 erforderlich',
      productionTitle: 'Produktion',
      productionDescription:
        'Füllen Sie mindestens eine Kennzahl aus, um den Bearbeitungsaufwand zu beschreiben.',
      productionRequiredCounter: '1 von 3 erforderlich',
      totalDurationLabel: 'Gesamte Bearbeitungsdauer',
      pieceDurationLabel: 'Bearbeitungsdauer pro Stück',
      duration: {
        hoursShort: 'Std',
        hoursLabel: 'Stunden',
        minutesShort: 'Min',
        minutesLabel: 'Minuten',
        secondsShort: 'Sek',
        secondsLabel: 'Sekunden',
        increase: '{{unit}} erhöhen',
        decrease: '{{unit}} verringern',
      },
      piecesLabel: 'Anzahl der Teile',
      volumeLabel: 'Spanvolumen [cm³] ',
      distanceLabel: 'Bearbeitete Strecke [m]',
      qualityTitle: 'Qualitätsüberwachung',
      qualityDescription:
        'Optionale Felder, um Qualitätskennzahlen mit den Schnittparametern zu verknüpfen.',
      malroundLabel: 'Rundlaufabweichung (µm)',
      malroundPlaceholder: 'Nicht angegeben',
      operationLabel: 'Operation',
      operationPlaceholder: 'Operation auswählen oder eingeben…',
      operatorLabel: 'Verantwortliche Bediener:in',
      cutParameters: {
        title: 'Schnittparameter',
        description: 'Optionale Schnittbedingungen für diesen Vorgang.',
        aeLabel: 'ae (mm)',
        apLabel: 'ap (mm)',
        vcLabel: 'vc (m/min)',
        fzLabel: 'fz (mm/z)',
        placeholder: '–',
      },
    },
    materialTab: {
      materialTitle: 'Bearbeitetes Material',
      materialDescription:
        'Werkstoff aus dem Katalog auswählen, um Empfehlungen zu verfeinern.',
      title: 'Werkstoff',
      picker: {
        label: 'Werkstoff',
        placeholder: 'Werkstoff auswählen…',
        searchPlaceholder: 'Nach Name oder Bezeichnung suchen…',
        noResults: 'Kein passender Werkstoff gefunden.',
        hint: 'Werkstoff fehlt? Fügen Sie einen im Tab Werkstoffe der Werkstatt hinzu.',
      },
      lot: {
        title: 'Chargendetails',
        optional: 'optional',
        supplierLabel: 'Lieferant',
        supplierPlaceholder: 'Name des Lieferanten',
        lotLabel: 'Chargennummer',
        lotPlaceholder: 'z. B. L2025-04',
        hint: 'Gleicher Lieferant und gleiche Charge für denselben Werkstoff werden serverseitig automatisch dedupliziert.',
      },
      empty: {
        title: 'Noch keine Werkstoffe',
        hint: 'Noch keine Werkstoffe — fügen Sie einen im Tab Werkstoffe der Werkstatt hinzu.',
      },
      prefill: {
        header: 'Vom Prozess vorausgefüllt',
        locked: 'Gesperrt durch die gewählte Prozessvorlage. Wählen Sie einen anderen Prozess zum Ändern.',
        lot: {
          supplier: 'Lieferant',
          lot: 'Charge',
        },
      },
      errors: {
        required: 'Bitte einen Werkstoff auswählen.',
      },
      supplierLabel: 'Lieferant',
      supplierPlaceholder: 'Name des Lieferanten',
      lotLabel: 'Chargennummer',
      lotPlaceholder: 'z. B. L2025-04',
    },
    machineTab: {
      sectionTitle: 'Verwendete Maschine',
      sectionDescription:
        'Wählen Sie eine gespeicherte Maschine aus oder füllen Sie die Angaben manuell aus.',
      dropdownLabel: 'Gespeicherte Maschine',
      dropdownPlaceholder: 'Wählen Sie eine Maschine',
      helperText:
        'Verwalten Sie den Maschinenkatalog oder legen Sie direkt im Manager einen neuen Eintrag an. Die untenstehenden Felder lassen sich für diesen Vorgang anpassen.',
      detailsTitle: 'Maschinendetails für diesen Vorgang',
      brandLabel: 'Marke',
      brandPlaceholder: 'z. B. DMG Mori',
      modelLabel: 'Modell',
      modelPlaceholder: 'z. B. DMC 65 monoBLOCK',
      serialLabel: 'Seriennummer / Kennung',
      serialPlaceholder: 'Interne oder Herstellerkennung',
      kafkaNameLabel: 'Signalquelle',
      summaryTitle: 'Ausgewählter Eintrag',
      summarySerial: 'SN: {{serial}}',
      openManager: 'Maschinenmanager öffnen',
      refreshList: 'Liste aktualisieren',
      listTitle: 'Gespeicherte Maschinen',
      listCount: '{{count}} Element(e)',
      emptyState: 'Noch keine Maschine gespeichert.',
      deleteSelection: 'Auswahl löschen',
    },
    holderTab: {
      title: 'Werkzeugaufnahme',
      description:
        'Identifizieren Sie die verwendete Aufnahme, um den Einfluss von Steifigkeit und Wuchtung auf die Standzeit zu verfolgen.',
      typeLabel: 'Aufnahmeart',
      brandLabel: 'Marke / Modell',
      brandPlaceholder: 'z. B. REGO-FIX powRgrip',
      options: {
        hotShrink: 'Heißschrumpfaufnahme',
        coldShrink: 'Kalt-Schrumpfaufnahme',
        collet: 'Spannzangenfutter',
        hydraulic: 'Hydraulisches Spannfutter',
        other: 'Sonstiges',
      },
    },
    clampingTab: {
      title: 'Werkstückspannung',
      description:
        'Geben Sie an, wie das Werkstück gehalten wird, um Vibrationen und mögliche Abweichungen zu interpretieren.',
      typeLabel: 'Spannart',
      brandLabel: 'Marke / Modell',
      brandPlaceholder: 'z. B. Hainbuch TOPlus mini',
      options: {
        vise: 'Schraubstock',
        clamp: 'Spannpratze',
        fixture: 'Spannvorrichtung',
        chuck: 'Spannfutter',
        rotarySteady: 'Mitlaufende Lünette',
        fixedSteady: 'Feste Lünette',
        collet: 'Spannzange',
        vacuum: 'Vakuum-Spannung',
        glue: 'Klebespannung',
        gel: 'Gel-Auflage',
        other: 'Sonstiges',
      },
    },
    lubricationTab: {
      title: 'Schmierung',
      description:
        'Beschreiben Sie Verfahren und Produkt, um deren Einfluss auf die Werkzeugstandzeit zu bewerten.',
      typeLabel: 'Schmierungsart',
      brandLabel: 'Marke / Produkt',
      brandPlaceholder: 'z. B. Blaser B-Cool',
      options: {
        dry: 'Trockenbearbeitung',
        oil: 'Reinöl',
        emulsion: 'Emulsion',
        mql: 'Minimalmengenschmierung (MMS)',
        co2: 'CO₂',
        air: 'Luftstrahl',
        other: 'Sonstiges',
      },
    },
    breakTab: {
      stateTitle: 'Beobachteter Zustand',
      stateDescription:
        'Beschreiben Sie den Werkzeugzustand zum Zeitpunkt der Meldung, um den Wechsel einzuordnen.',
      stateLabel: 'Werkzeugzustand',
      reasonTitle: 'Wechsel & Nachverfolgung',
      reasonDescription:
        'Wählen Sie die Wechselgründe und hinterlassen Sie eine Notiz für das Produktionsteam.',
      reasonLabel: 'Wechselgrund',
      commentLabel: 'Notiz',
      commentPlaceholder: 'Freie Notiz für das Produktionsteam',
      stateOptions: {
        new: 'Neu',
        worn: 'Verschlissen',
        broken: 'Gebrochen',
        unknown: 'Unbekannt',
      },
      reasonOptions: {
        completeBrokenTool: 'Werkzeug vollständig gebrochen',
        brokenTeeth: 'Zahn/Zähne gebrochen',
        brokenPart: 'Werkstück gebrochen',
        burr: 'Gratbildung',
        badSurface: 'Schlechte Oberflächenqualität',
        badDimension: 'Maßabweichung',
        endOfSeries: 'Serienende (keine Teile mehr)',
        productionStop: 'Produktionsstopp trotz laufender Serie',
        noise: 'Ungewöhnliches Bearbeitungsgeräusch',
        maxToolLife: 'Geplante Standzeit erreicht',
        partAppearance: 'Werkstückoptik mangelhaft',
        vibration: 'Vibrationen',
        other: 'Sonstiges',
      },
    },
    uploadImage: {
      pageTitle: 'Bild hochladen',
      pageDescription: 'Laden Sie ein Bild mit einem DataMatrix-Code hoch',
      breadcrumb: 'Hochladen',
      dropZone: {
        prompt: 'Ziehen Sie ein Bild hierher',
        browse: 'Dateien durchsuchen',
        hint: 'Unterstützt PNG, JPG und andere Bildformate',
      },
      statusIdle: 'Kein Bild geladen',
      statusLoading: 'Bild wird geladen…',
      errorDecode: 'Fehler beim Dekodieren des Bildes.',
      errorNoDimension: 'Fehler: Das Bild enthält keine Abmessungen.',
      statusScanning: 'Wird gescannt…',
      statusDetected: 'Kennung erkannt: {{id}}',
      statusNotFound: 'Kein DataMatrix erkannt. Versuchen Sie ein anderes Bild.',
      imageAlt: 'Importierte Vorschau',
    },
    addToolModal: {
      title: 'Neues Werkzeug erstellen',
      description: 'Registrieren Sie ein neues Werkzeug in der Datenbank, bevor Sie scannen.',
    },
    materials: {
      acier: 'Stahl',
      acier_trempe: 'Gehärteter Stahl',
      aluminium: 'Aluminium',
      chrome: 'Chrom-Kobalt/Phynox/Stellite',
      titane: 'Titan',
      fonte: 'Gusseisen',
      nickel: 'Nickellegierung',
      bronze: 'Bronze',
      laiton: 'Messing',
      or: 'Gold',
      super: 'Superlegierungen',
      argent: 'Silber',
      cube: 'CuBe',
      cuivre: 'Kupfer',
      durnico: 'Durnico',
      laiton_sans_plomb: 'Bleifreies Messing',
      metal_lourd: 'Schwermetall (Wolfram)',
      céramique: 'Keramik',
      composite_carbone: 'Kohlefaserverbundwerkstoff',
      composite_verre: 'Glasfaserverbundwerkstoff',
      graphite: 'Graphit',
      palladium: 'Palladium',
      platine: 'Platin',
      polymere: 'Polymer',
      tantale: 'Tantal',
      autre: 'Andere',
      lt700: '<700 N/mm²',
      gt700: '>700 N/mm²',
      finemac: 'Finemac',
      inox: 'Rostfrei',
      lt58: 'Gehärtet <58HRc',
      lt65: 'Gehärtet <65HRc',
      lt8: '<8%Si',
      gt8: '>8%Si',
    },
    materialLotManager: {
      picker: {
        sectionTitle: 'Nach Werkstoff durchsuchen',
        label: 'Werkstoff',
        placeholder: 'Werkstoff auswählen…',
        count: '{{count}} Werkstoffe verfügbar',
      },
      form: {
        sectionTitle: 'Charge finden oder erstellen',
        idempotent: 'Idempotent',
        fields: {
          supplier: {
            label: 'Lieferant',
            placeholder: 'z. B. Outokumpu',
            optional: '(optional)',
          },
          lotNumber: {
            label: 'Chargennummer',
            placeholder: 'z. B. L2025-04-001',
            optional: '(optional)',
          },
        },
        idempotencyHint:
          'Derselbe Lieferant und dieselbe Chargennummer für diesen Werkstoff liefern immer dieselbe Chargen-ID.',
        submit: {
          findOrCreate: 'Finden oder erstellen',
          saving: 'Wird gespeichert…',
          reset: 'Zurücksetzen',
        },
        result: {
          title: 'Charge bereit',
          idLabel: 'id #{{id}}',
          noSupplier: 'Kein Lieferant',
        },
      },
      list: {
        title: 'Chargen für {{name}}',
        count: '{{count}} Chargen',
        loading: 'Chargen werden geladen',
        emptyTitle: 'Noch keine Chargen',
        emptyHint: 'Erstellen Sie eine mit dem Formular auf der linken Seite.',
        row: {
          noSupplier: 'Kein Lieferant',
        },
      },
      empty: {
        title: 'Werkstoff auswählen, um Chargen anzuzeigen',
        hint: 'Chargen sind einem einzelnen Werkstoff zugeordnet — wählen Sie oben einen aus der Dropdown-Liste, um Lieferantenchargen hinzuzufügen oder zu durchsuchen.',
      },
      errors: {
        loadFailed: 'Chargen für diesen Werkstoff konnten nicht geladen werden.',
        saveFailed: 'Charge konnte nicht gespeichert werden. Bitte erneut versuchen.',
      },
    },
    workshop: {
      hero: {
        badge: 'Werkstatt',
        title: 'Werkstatt',
        description: 'Werkzeuge in der Datenbank registrieren und Maschinen verwalten.',
        breadcrumb: 'Werkstatt',
      },
      tabs: {
        tool: 'Werkzeug registrieren',
        machines: 'Maschinen',
        articles: 'Artikel',
        materials: 'Werkstoffe',
        lots: 'Materialchargen',
        processes: 'Prozesse',
      },
      toolForm: {
        mode: {
          single: 'Einzeln',
          batch: 'Stapel',
        },
        single: {
          sectionTitle: 'Werkzeuginformationen',
        },
        batch: {
          sectionTitle: 'Stapelregistrierung',
          addToolId: 'Werkzeug-ID hinzufügen',
          toolIdPlaceholder: 'Werkzeug-ID #{{n}}',
        },
        fields: {
          toolId: {
            label: 'Werkzeug-ID',
            placeholder: 'z.B. EGO-001',
          },
          reference: {
            label: 'Referenz',
            placeholder: 'z.B. LB-12345',
          },
          company: {
            label: 'Unternehmen',
            placeholder: 'Unternehmensname',
            autoFilled: 'Automatisch ausgefüllt',
          },
        },
        info: {
          single:
            'Erstellt einen neuen Werkzeugeintrag in der Datenbank. Sofort für Rückverfolgungsscans verfügbar.',
          batch:
            'Erstellt mehrere Werkzeuge mit derselben Referenz. Ergebnisse werden pro Werkzeug-ID angezeigt.',
        },
        submit: {
          single: 'Werkzeug erstellen',
          batch: '{{count}} Werkzeuge erstellen',
        },
        success: 'Werkzeug erfolgreich erstellt.',
        errors: {
          default: 'Werkzeug konnte nicht erstellt werden.',
        },
        scan: {
          button: 'DataMatrix scannen',
          buttonNext: 'Nächsten scannen',
          buttonRescan: 'Erneut scannen',
          connecting: 'Verbinde…',
          scanning: 'Scan läuft…',
          cancel: 'Abbrechen',
          waitingOverlay: 'Auf DataMatrix warten — Werkzeug vor die Kamera halten',
          statusDisconnected: 'Keyence nicht verbunden',
          statusConnected: 'Verbunden · {{ip}}',
          statusDetected: 'DataMatrix erkannt — ID oben bestätigen',
          statusReady: 'Bereit für nächsten Scan',
          successFlash: '{{toolId}} erfolgreich registriert',
        },
        session: {
          title: 'Diese Sitzung',
          count: '{{n}} registriert',
          clear: 'Löschen',
        },
        autoRegister: {
          label: 'Automatisch registrieren',
          description: 'Scan → Registrierung → nächster Scan automatisch — kein Klick zwischen Werkzeugen',
          registering: 'Registrierung…',
          registeringFlash: '{{toolId}} wird registriert…',
        },
      },
    },
  },
};
